import type { AgentEvent } from '@clawlet/gateway-client';
import { type ToolCallEntry, type TodoItem, isSubagentSessionKey, useAppStore } from '../stores/appStore';
import { isTeamSession, notifyTeamDelta, notifyTeamDone, notifyTeamThinking, notifyTeamToolCall } from './teamSessionRegistry';

type ThinkingTracker = {
  prefix: string;
  lastBlockText: string;
};

type SubagentTextTracker = {
  committedText: string;
  currentMessageText: string;
};

const SUBAGENT_MESSAGE_SEPARATOR = '\n\n---\n\n';
const TOOL_RUNNING_MIN_VISIBLE_MS = 600;
const thinkingBlocksBySession: Record<string, ThinkingTracker> = {};
const turnStartedBySession: Record<string, boolean> = {};
const subagentTextTrackerBySession: Record<string, SubagentTextTracker> = {};
const seenEventsByRun = new Map<string, Set<number>>();
const pendingToolCompletionTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Grace period (ms) before honoring error events for subagents.
 *
 * The gateway sends false `lifecycle: error` and `chat: error` events for
 * subagent session keys through the main session's WebSocket ~10 seconds
 * after spawn, while the coder subagent is still running fine.  During this
 * window we defer error marking: a setTimeout fires after the grace period
 * and only marks the subagent as error if it is *still* in 'running' state
 * (i.e. it never completed on its own — a genuine failure).
 */
const SUBAGENT_ERROR_GRACE_MS = 120_000; // 2 minutes

/** De-dup set so we schedule at most one deferred error check per session key. */
const pendingDeferredErrors = new Set<string>();

/**
 * Decide whether a subagent error event should be applied right now.
 *
 * Returns `true` if the caller should proceed with marking the error and
 * resetting streaming state.  Returns `false` if the error is deferred
 * (a timeout is scheduled internally) and the caller should skip all
 * error-related side-effects.
 */
export function shouldApplySubagentError(sessionKey: string): boolean {
  const state = useAppStore.getState();
  const info = state.subagentRuns[sessionKey];
  if (!info || info.status !== 'running') return false;

  const elapsed = Date.now() - info.startTime;
  if (elapsed >= SUBAGENT_ERROR_GRACE_MS) {
    // Past the grace window — treat the error as genuine.
    return true;
  }

  // Within the grace window — defer.
  if (!pendingDeferredErrors.has(sessionKey)) {
    pendingDeferredErrors.add(sessionKey);
    const delay = SUBAGENT_ERROR_GRACE_MS - elapsed;
    setTimeout(() => {
      pendingDeferredErrors.delete(sessionKey);
      const current = useAppStore.getState().subagentRuns[sessionKey];
      if (current && current.status === 'running') {
        // Still running after the full grace period — genuine failure.
        useAppStore.getState().markSubagentError(sessionKey);
      }
    }, delay);
  }

  return false;
}

/**
 * Extract text from an AgentEvent data payload.
 *
 * Different model providers populate different fields:
 *  - Anthropic (Opus): `data.text`
 *  - OpenAI (gpt-5.4): `data.content` (string or structured content blocks)
 *
 * This helper unifies extraction so subagent streaming works regardless
 * of the upstream model.
 */
function extractAgentEventText(data: Record<string, any>): string | null {
  // 1. Direct text field (Anthropic style)
  if (typeof data.text === 'string' && data.text.length > 0) {
    return data.text;
  }

  // 2. Content field — string (OpenAI style)
  if (typeof data.content === 'string' && data.content.length > 0) {
    return data.content;
  }

  // 3. Content field — structured content blocks array
  //    e.g. [{ type: "text", text: "Hello" }, ...]
  if (Array.isArray(data.content)) {
    const parts = data.content
      .filter((block: any) => block?.type === 'text' && typeof block.text === 'string')
      .map((block: any) => block.text as string);
    if (parts.length > 0) {
      return parts.join('\n');
    }
  }

  // 4. Message field (fallback)
  if (typeof data.message === 'string' && data.message.length > 0) {
    return data.message;
  }

  // 5. Delta field (incremental streaming)
  if (typeof data.delta === 'string' && data.delta.length > 0) {
    return data.delta;
  }

  return null;
}

function getToolStatus(data: Record<string, any>): ToolCallEntry['status'] {
  const phase = String(data.phase ?? data.state ?? data.status ?? '').toLowerCase();

  if (phase.includes('error') || data.error != null) {
    return 'error';
  }

  const hasResult =
    data.result != null || data.output != null || data.completed === true || data.done === true;

  if (
    hasResult ||
    phase === 'done' ||
    phase === 'completed' ||
    phase === 'complete' ||
    phase === 'end' ||
    phase === 'result' ||
    phase === 'success' ||
    phase === 'ok'
  ) {
    return 'done';
  }

  return 'running';
}

function normalizePlanStatus(status: unknown): TodoItem['status'] {
  const normalized = String(status ?? '').trim().toLowerCase().replace(/[-\s]/g, '_');
  if (normalized === 'completed' || normalized === 'complete' || normalized === 'done') return 'completed';
  if (normalized === 'inprogress' || normalized === 'in_progress' || normalized === 'running') return 'in_progress';
  return 'pending';
}

function planTodosFromData(data: Record<string, any>): TodoItem[] {
  const rawItems = Array.isArray(data.plan)
    ? data.plan
    : Array.isArray(data.todos)
      ? data.todos
      : [];

  return rawItems.flatMap((item: any) => {
    const rawContent = item?.content ?? item?.step ?? item?.text;
    if (typeof rawContent !== 'string') return [];
    const content = rawContent.trim();
    if (!content) return [];
    const activeForm = typeof item?.activeForm === 'string' && item.activeForm.trim()
      ? item.activeForm
      : content;
    return [{
      content,
      status: normalizePlanStatus(item?.status),
      activeForm,
    }];
  });
}

function markEventSeen(runId?: string, seq?: number): boolean {
  if (!runId || typeof seq !== 'number') {
    return false;
  }

  let seen = seenEventsByRun.get(runId);
  if (!seen) {
    seen = new Set<number>();
    seenEventsByRun.set(runId, seen);
  }

  if (seen.has(seq)) {
    return true;
  }

  seen.add(seq);
  return false;
}

function toolCompletionTimerKey(sessionKey: string, toolCallId: string): string {
  return `${sessionKey}\0${toolCallId}`;
}

function clearPendingToolCompletionTimer(sessionKey: string, toolCallId: string): void {
  const key = toolCompletionTimerKey(sessionKey, toolCallId);
  const timer = pendingToolCompletionTimers.get(key);
  if (!timer) return;

  clearTimeout(timer);
  pendingToolCompletionTimers.delete(key);
}

function clearPendingToolCompletionTimers(sessionKey?: string | null): void {
  for (const [key, timer] of pendingToolCompletionTimers) {
    if (!sessionKey || key.startsWith(`${sessionKey}\0`)) {
      clearTimeout(timer);
      pendingToolCompletionTimers.delete(key);
    }
  }
}

function getOrCreateSubagentTextTracker(sessionKey: string): SubagentTextTracker {
  const existing = subagentTextTrackerBySession[sessionKey];
  if (existing) {
    return existing;
  }

  const tracker: SubagentTextTracker = {
    committedText: '',
    currentMessageText: '',
  };
  subagentTextTrackerBySession[sessionKey] = tracker;
  return tracker;
}

function buildSubagentDisplayText(tracker: SubagentTextTracker): string {
  if (tracker.committedText && tracker.currentMessageText) {
    return `${tracker.committedText}${SUBAGENT_MESSAGE_SEPARATOR}${tracker.currentMessageText}`;
  }

  return tracker.committedText || tracker.currentMessageText;
}

function commitSubagentCurrentMessage(tracker: SubagentTextTracker): void {
  if (!tracker.currentMessageText.trim()) {
    return;
  }

  tracker.committedText = tracker.committedText
    ? `${tracker.committedText}${SUBAGENT_MESSAGE_SEPARATOR}${tracker.currentMessageText}`
    : tracker.currentMessageText;
  tracker.currentMessageText = '';
}

function isSameSubagentMessage(previousText: string, nextText: string): boolean {
  if (!previousText || !nextText) {
    return true;
  }

  if (nextText.startsWith(previousText) || previousText.startsWith(nextText)) {
    return true;
  }

  const prefixLength = Math.min(previousText.length, nextText.length, 80);
  if (prefixLength === 0) {
    return true;
  }

  return previousText.slice(0, prefixLength) === nextText.slice(0, prefixLength);
}

export function mergeSubagentStreamingText(sessionKey: string, text: string): string {
  const tracker = getOrCreateSubagentTextTracker(sessionKey);

  if (
    tracker.currentMessageText.trim().length > 0 &&
    text.trim().length > 0 &&
    !isSameSubagentMessage(tracker.currentMessageText, text)
  ) {
    commitSubagentCurrentMessage(tracker);
  }

  tracker.currentMessageText = text;
  return buildSubagentDisplayText(tracker);
}

export function sealSubagentStreamingMessage(sessionKey: string): string | null {
  const tracker = subagentTextTrackerBySession[sessionKey];
  if (!tracker) {
    return null;
  }

  commitSubagentCurrentMessage(tracker);
  const nextText = buildSubagentDisplayText(tracker);
  return nextText || null;
}

function resetSubagentStreamingTracker(sessionKey?: string | null): void {
  if (!sessionKey) {
    return;
  }

  delete subagentTextTrackerBySession[sessionKey];
}

export function resetSharedAgentTurnState(sessionKey?: string | null, runId?: string | null): void {
  if (sessionKey) {
    delete turnStartedBySession[sessionKey];
    delete thinkingBlocksBySession[sessionKey];
    resetSubagentStreamingTracker(sessionKey);
    clearPendingToolCompletionTimers(sessionKey);
  }

  if (runId) {
    seenEventsByRun.delete(runId);
  }
}

export function resetAllSharedAgentTurnState(): void {
  for (const key of Object.keys(turnStartedBySession)) {
    delete turnStartedBySession[key];
  }

  for (const key of Object.keys(thinkingBlocksBySession)) {
    delete thinkingBlocksBySession[key];
  }

  for (const key of Object.keys(subagentTextTrackerBySession)) {
    delete subagentTextTrackerBySession[key];
  }

  seenEventsByRun.clear();
  clearPendingToolCompletionTimers();
}

export function handleSharedAgentEvent(event: AgentEvent): void {
  const sessionKey = event.sessionKey;
  if (!sessionKey) {
    console.warn('[session-routing] dropping agent event with missing sessionKey', { stream: event.stream });
    return;
  }
  const state = useAppStore.getState();

  if (markEventSeen(event.runId, event.seq)) {
    return;
  }

  const data = event.data ?? {};
  console.debug('[agentEventHandler] agent event:', event.stream, event.data?.phase, { sessionKey });

  // ── Team Channel Session Routing ──────────────────────────────
  if (isTeamSession(sessionKey)) {
    // Route relevant events to the team channel registry
    if (event.stream === 'assistant') {
      const text = extractAgentEventText(data);
      if (text) {
        notifyTeamDelta(sessionKey, text);
      }
    } else if (event.stream === 'thinking') {
      // Thinking is handled in the fall-through section below, which
      // accumulates blocks via thinkingBlocksBySession and then notifies
      // the team registry with the full accumulated content.
    } else if (event.stream === 'tool_call' || event.stream === 'tool_result' || event.stream === 'tool') {
      const nameSource = data.name ?? data.tool ?? data.toolName ?? 'tool';
      const name = typeof nameSource === 'string' ? nameSource : String(nameSource);
      const rawId = data.toolCallId ?? data.id ?? `${event.runId}:${event.seq}`;
      const toolCallId = typeof rawId === 'string' ? rawId : String(rawId);
      const status: 'running' | 'done' | 'error' = event.stream === 'tool_result'
        ? 'done'
        : event.stream === 'tool'
          ? getToolStatus(data)
          : 'running';
      const inputRaw = data.input ?? data.params;
      const outputRaw = data.output ?? data.result;
      notifyTeamToolCall(sessionKey, {
        toolCallId,
        name,
        status,
        input: inputRaw != null ? (typeof inputRaw === 'string' ? inputRaw : JSON.stringify(inputRaw)) : undefined,
        output: outputRaw != null ? (typeof outputRaw === 'string' ? outputRaw : JSON.stringify(outputRaw)) : undefined,
      });
    } else if (event.stream === 'lifecycle') {
      const phase = String(data.phase ?? '').toLowerCase();
      if (phase === 'end') {
        // Grab accumulated text from streaming state and notify done.
        // Only fire if there's actual text — prevents double-firing when
        // useGateway.ts already called notifyTeamDone on the chat final event,
        // and prevents premature done on empty lifecycle ends (tool-call rounds).
        const finalText = (state.streamingMessage[sessionKey] ?? '').trim();
        if (finalText) {
          notifyTeamDone(sessionKey, finalText);
          seenEventsByRun.delete(event.runId);
        }
      }
    }
    // Still fall through for normal processing (so streaming still shows in appStore)
    // but skip expensive subagent processing
  }

  if (isSubagentSessionKey(sessionKey)) {
    if (event.stream === 'lifecycle') {
      const phase = String(data.phase ?? '').toLowerCase();
      if (phase === 'start') {
        state.upsertSubagent(sessionKey);
        const sealedText = sealSubagentStreamingMessage(sessionKey);
        if (sealedText) {
          state.setSubagentStreamingText(sessionKey, sealedText);
        }
      } else if (phase === 'end') {
        state.markSubagentCompleted(sessionKey);
        resetSubagentStreamingTracker(sessionKey);
        seenEventsByRun.delete(event.runId);
      } else if (phase === 'error') {
        if (shouldApplySubagentError(sessionKey)) {
          state.markSubagentError(sessionKey);
          resetSubagentStreamingTracker(sessionKey);
          seenEventsByRun.delete(event.runId);
        }
        // else: deferred — don't reset streaming state so the subagent
        // can continue streaming text if the error was a false positive.
      }
      return;
    }

    if (event.stream === 'assistant') {
      state.upsertSubagent(sessionKey);
      const text = extractAgentEventText(data);
      if (text && text.length > 0) {
        state.setSubagentStreamingText(sessionKey, mergeSubagentStreamingText(sessionKey, text));
      }
      return;
    }

    if (event.stream === 'tool') {
      state.upsertSubagent(sessionKey);
    }
    return;
  }

  if (event.stream === 'lifecycle') {
    const phase = String(data.phase ?? '').toLowerCase();
    if (phase === 'start') {
      clearPendingToolCompletionTimers(sessionKey);
      const pendingStream = (state.streamingMessage[sessionKey] ?? '').trim();
      if (pendingStream) {
        const pendingToolCalls = state.toolCalls[sessionKey];
        state.addMessage(sessionKey, {
          role: 'assistant',
          content: pendingStream,
          timestamp: new Date().toISOString(),
          toolCalls:
            pendingToolCalls && pendingToolCalls.length > 0
              ? [...pendingToolCalls]
              : undefined,
        });
        state.setStreamingMessage(sessionKey, null);
      }

      // Session reset animation: advance from Phase 1 ("Saving session...")
      // to Phase 2 ("Starting new session...") when the actual new session starts.
      // Only advance if the lifecycle event has an explicit sessionKey that matches
      // (not a fallback to activeSessionKey) and isn't from a temp summarization session.
      const resetPhase = state.sessionResetPhase[sessionKey];
      if (resetPhase === 'saving' && event.sessionKey && !event.sessionKey.startsWith('temp:')) {
        state.advanceSessionResetPhase(sessionKey);
      }

      if (!turnStartedBySession[sessionKey]) {
        turnStartedBySession[sessionKey] = true;
        state.clearRunState(sessionKey);
        delete thinkingBlocksBySession[sessionKey];
      } else {
        state.clearToolCalls(sessionKey);
      }

      state.setThinking(sessionKey, true);
      return;
    }

    if (phase === 'end' || phase === 'error') {
      state.setThinking(sessionKey, false);
      delete turnStartedBySession[sessionKey];
      seenEventsByRun.delete(event.runId);

      // Update context window fill from cli-bridge's lifecycle end event.
      // contextFill = input_tokens + cache_read + cache_creation from the last turn.
      const contextFill = typeof data.contextFill === 'number' ? data.contextFill : 0;
      if (contextFill > 0) {
        state.setSessionContextFill(sessionKey, contextFill);
      }
      return;
    }

    return;
  }

  if (event.stream === 'warning') {
    const text = typeof data.text === 'string' ? data.text : '';
    const code = typeof data.code === 'string' ? data.code : '';
    if (text) {
      state.addWarning(sessionKey, { text, code });
    }
    return;
  }

  if (event.stream === 'plan') {
    state.setSessionTodos(sessionKey, planTodosFromData(data));
    return;
  }

  if (event.stream === 'thinking') {
    state.setThinking(sessionKey, true);
    const text =
      typeof data.text === 'string'
        ? data.text
        : typeof data.thinking === 'string'
          ? data.thinking
          : typeof data.content === 'string'
            ? data.content
            : null;

    if (text) {
      const tracker = thinkingBlocksBySession[sessionKey] ?? {
        prefix: '',
        lastBlockText: '',
      };
      thinkingBlocksBySession[sessionKey] = tracker;

      if (tracker.lastBlockText.length > 0 && text.length < tracker.lastBlockText.length) {
        tracker.prefix = tracker.prefix
          ? `${tracker.prefix}\n\n---\n\n${tracker.lastBlockText}`
          : tracker.lastBlockText;
      }

      tracker.lastBlockText = text;

      const fullContent = tracker.prefix ? `${tracker.prefix}\n\n---\n\n${text}` : text;
      state.setThinkingContent(sessionKey, fullContent);

      // For team sessions, forward the accumulated thinking content to the
      // team registry so the TeamChannelView can display it in real-time.
      if (isTeamSession(sessionKey)) {
        notifyTeamThinking(sessionKey, fullContent);
      }
    }
    return;
  }

  if (event.stream === 'assistant') {
    state.setThinking(sessionKey, false);
    return;
  }

  if (event.stream === 'tool') {
    state.setThinking(sessionKey, false);

    const rawId = data.toolCallId ?? data.id ?? `${event.runId}:${event.seq}`;
    const toolCallId = typeof rawId === 'string' ? rawId : String(rawId);

    const nameSource = data.name ?? data.tool ?? data.toolName ?? 'tool';
    const name = typeof nameSource === 'string' ? nameSource : String(nameSource);

    const status = getToolStatus(data);
    const existingCalls = state.toolCalls[sessionKey] ?? [];
    const existingEntry = existingCalls.find((tc) => tc.toolCallId === toolCallId);
    const now = Date.now();
    const startedAt = existingEntry?.startedAt ?? (status === 'running' ? now : undefined);
    const elapsed = startedAt ? now - startedAt : TOOL_RUNNING_MIN_VISIBLE_MS;
    const shouldDelayDone =
      status === 'done' &&
      existingEntry?.status === 'running' &&
      elapsed < TOOL_RUNNING_MIN_VISIBLE_MS;

    if (status === 'running') {
      clearPendingToolCompletionTimer(sessionKey, toolCallId);
    }

    const error = (() => {
      if (!data.error) return undefined;
      console.error('[agentEventHandler] Tool call failed:', data.error);
      if (typeof data.error === 'object' && data.error !== null) {
        return { message: String(data.error), ...data.error };
      }
      return { message: String(data.error) };
    })();

    const metaKeys = new Set([
      'phase',
      'state',
      'status',
      'name',
      'tool',
      'toolName',
      'toolCallId',
      'id',
      'result',
      'output',
      'partialResult',
      'error',
      'done',
      'completed',
      'meta',
      'isError',
    ]);

    let params: Record<string, unknown> | undefined = data.params ?? data.input as Record<string, unknown> | undefined;
    if (!params) {
      const extra: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(data)) {
        if (!metaKeys.has(key) && value != null) {
          extra[key] = value;
        }
      }
      if (Object.keys(extra).length > 0) {
        params = extra;
      }
    }

    const entry: ToolCallEntry = {
      toolCallId,
      name,
      timestamp: now,
      status: shouldDelayDone ? 'running' : status,
      runId: event.runId,
      seq: event.seq,
      serverTs: event.ts,
      params: params ?? existingEntry?.params,
      result: data.result ?? data.output ?? existingEntry?.result,
      partialResult: typeof data.partialResult === 'string' ? data.partialResult : existingEntry?.partialResult,
      parentToolCallId: typeof data.parentToolCallId === 'string' ? data.parentToolCallId : existingEntry?.parentToolCallId,
      error,
      startedAt,
      completedAt: status !== 'running' && !shouldDelayDone ? now : existingEntry?.completedAt,
    };

    state.addToolCall(sessionKey, entry);
    if (name === 'TodoWrite' && Array.isArray(params?.todos)) {
      state.setSessionTodos(sessionKey, params.todos as TodoItem[]);
    }

    if (shouldDelayDone) {
      clearPendingToolCompletionTimer(sessionKey, toolCallId);
      const delay = TOOL_RUNNING_MIN_VISIBLE_MS - elapsed;
      const finalEntry: ToolCallEntry = {
        ...entry,
        status,
        completedAt: now,
      };
      const timer = setTimeout(() => {
        pendingToolCompletionTimers.delete(toolCompletionTimerKey(sessionKey, toolCallId));
        useAppStore.getState().addToolCall(sessionKey, finalEntry);
      }, delay);
      pendingToolCompletionTimers.set(toolCompletionTimerKey(sessionKey, toolCallId), timer);
    }
  }
}
